import { uuidModule } from "@/modules/uuid";
import {
  SpaceAccountNoteModelData,
  SpaceAccountNoteUpsertedSyncUpdateValue,
} from "@/store/recent-items/types";
import {
  BaseSyncOperation,
  BaseSyncOperationParams,
} from "@/store/sync/operations/BaseSyncOperation";
import {
  buildSuggestionStatus,
  getRenderableSuggestionData,
  NoteSuggestionStatus,
} from "@/store/note/suggestion-data";
import { IRejectNoteSuggestionOperation } from "@/store/sync/operations/types";
import {
  SyncModelData,
  OptimisticSyncUpdate,
  SyncOperationKind,
  SyncUpdateValue,
} from "@/store/sync/types";

type RejectNoteSuggestionOptimisticData = {
  spaceAccountNote: SyncUpdateValue<SpaceAccountNoteModelData>;
};

export class RejectNoteSuggestionOperation extends BaseSyncOperation<IRejectNoteSuggestionOperation> {
  private optimisticData: RejectNoteSuggestionOptimisticData;

  get operationKind(): SyncOperationKind {
    return "REJECT_NOTE_SUGGESTION";
  }

  constructor({
    optimisticData,
    ...params
  }: BaseSyncOperationParams<IRejectNoteSuggestionOperation> & {
    optimisticData: RejectNoteSuggestionOptimisticData;
  }) {
    super(params);
    this.optimisticData = optimisticData;
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const spaceAccountNote = this.optimisticData.spaceAccountNote;
    const suggestionData = getRenderableSuggestionData({
      suggestionData: spaceAccountNote.model_data.suggestion_data,
    });
    if (!suggestionData) return [];

    const spaceAccountNoteUpsertedValue: SpaceAccountNoteUpsertedSyncUpdateValue = {
      model_id: spaceAccountNote.model_id,
      model_kind: "SPACE_ACCOUNT_NOTE",
      model_version: spaceAccountNote.model_version ?? 0,
      model_data: {
        ...spaceAccountNote.model_data,
        suggestion_data: buildSuggestionStatus({
          suggestionData,
          status: NoteSuggestionStatus.Rejected,
        }),
      },
      model_scopes: spaceAccountNote.model_scopes,
    };
    const spaceAccountNoteUpserted: OptimisticSyncUpdate<SpaceAccountNoteModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: spaceAccountNoteUpsertedValue,
    };
    return [spaceAccountNoteUpserted];
  }

  async triggerRecompute() {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountNoteId = uuidModule.resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId,
      noteId: this.payload.note_id,
    });
    await this.store.spaceAccountNotes.recompute(spaceAccountNoteId);
  }
}
